import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Users, UserPlus, Copy, Trash2, ShieldAlert, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const err = (e, f) => e?.response?.data?.detail?.message || e?.response?.data?.detail || f;
const BLANK_ONE = { first_name: '', last_name: '', email: '', date_of_birth: '',
  grade_level: '', parent_name: '', parent_email: '' };

/** The roster: who's actually in this organization, and how they got their access. */
export const RosterPanel = ({ orgId, accent = '#22d3ee', canManage, onChanged }) => {
  const { getAuthHeader } = useAuth();
  const [people, setPeople] = useState([]);
  const [seats, setSeats] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('paste');
  const [programId, setProgramId] = useState('none');
  const [paste, setPaste] = useState('');
  const [one, setOne] = useState(BLANK_ONE);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    const q = filter === 'all' ? '' : `?program_id=${filter}`;
    axios.get(`${API}/orgs/${orgId}/participants${q}`, getAuthHeader())
      .then(r => { setPeople(r.data.participants); setSeats(r.data.seats); }).catch(() => {});
    axios.get(`${API}/orgs/${orgId}/programs`, getAuthHeader())
      .then(r => setPrograms(r.data.programs)).catch(() => {});
  }, [orgId, filter, getAuthHeader]);
  useEffect(load, [load]);

  const add = async () => {
    setSaving(true);
    try {
      const payload = { program_id: programId === 'none' ? null : programId };
      if (mode === 'paste') payload.paste = paste;
      else payload.participants = [one];
      const r = await axios.post(`${API}/orgs/${orgId}/participants`, payload, getAuthHeader());
      setResult(r.data);
      setPaste(''); setOne(BLANK_ONE);
      load(); onChanged?.();
    } catch (e) { toast.error(err(e, 'Could not add those people')); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/orgs/${orgId}/participants/${id}`, getAuthHeader());
      toast.success('Taken off this roster'); load(); onChanged?.();
    } catch (e) { toast.error(err(e, 'Could not remove them')); }
  };

  const copyAll = () => {
    const lines = (result?.added || []).map(a => [a.name, a.email || '-',
      a.starting_password || (a.managed ? 'guardian-managed, no login' : '-')].join('  '));
    navigator.clipboard?.writeText(lines.join('\n'));
    toast.success('Copied, keep it somewhere safe');
  };

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/60 p-5 md:p-6"
      data-testid="roster-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Users className="w-4 h-4" style={{ color: accent }} />
        <h2 className="font-outfit text-xl font-semibold text-white">Participants</h2>
        <span className="text-xs text-slate-300">{seats?.label}</span>
        {canManage && (
          <Button onClick={() => { setResult(null); setOpen(true); }} data-testid="add-participants-btn"
            className="ml-auto rounded-full text-slate-900 font-semibold" style={{ background: accent }}>
            <UserPlus className="w-4 h-4 mr-1.5" /> Add participants
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-4 text-xs">
        {[{ id: 'all', name: 'Everyone' }, ...programs].map(p => (
          <button key={p.id} onClick={() => setFilter(p.id)} data-testid={`roster-filter-${p.id}`}
            className={`rounded-full px-3 py-1 border transition-colors ${filter === p.id
              ? 'border-white/40 text-white bg-white/[0.06]' : 'border-white/10 text-slate-300 hover:text-white'}`}>
            {p.name}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {people.length === 0 && (
          <p className="text-sm text-slate-300" data-testid="roster-empty">
            Nobody here yet. Add them in one go, paste a list, or hand out a program code.
          </p>
        )}
        {people.map(p => (
          <motion.div key={p.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="flex flex-wrap items-center gap-3 rounded-xl bg-slate-800/40 px-4 py-2.5"
            data-testid={`participant-${p.id}`}>
            <span className="text-sm text-white">{p.full_name}</span>
            <span className="text-xs text-slate-300">{p.age}</span>
            {p.email && !p.email.includes('futurepath.local') && (
              <span className="text-xs text-slate-300">{p.email}</span>
            )}
            <span className="text-xs text-slate-300">{p.state}</span>
            {p.guardian && (
              <span className="text-xs text-slate-300">guardian: {p.guardian.full_name}</span>
            )}
            {p.programs?.length > 0 && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={{ background: `${accent}22`, color: accent }}>
                {p.programs.join(', ')}
              </span>
            )}
            {p.enrollment_status === 'pending_consent' && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300">
                <ShieldAlert className="w-3 h-3" /> consent needed
              </span>
            )}
            {canManage && (
              <button onClick={() => remove(p.id)} data-testid={`remove-participant-${p.id}`}
                className="ml-auto text-slate-300 hover:text-rose-300 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </motion.div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit text-xl">Add participants</DialogTitle>
          </DialogHeader>

          {result ? (
            <div className="space-y-4" data-testid="add-result">
              <p className="text-sm text-white flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-400" />
                {result.added.length} added{result.program ? ` to ${result.program}` : ''}
                {result.skipped.length ? ` · ${result.skipped.length} skipped` : ''}
              </p>
              <div className="rounded-xl border border-white/10 bg-slate-800/40 p-3 max-h-64 overflow-y-auto">
                {result.added.map((a, i) => (
                  <div key={i} className="text-xs text-slate-300 py-1 flex flex-wrap gap-3">
                    <span className="text-white">{a.name}</span>
                    <span className="text-slate-300">{a.email || 'no login'}</span>
                    {a.starting_password && (
                      <span className="font-mono text-emerald-300">{a.starting_password}</span>
                    )}
                    {a.managed && <span className="text-amber-300">guardian consent needed</span>}
                    {a.role === 'parent' && <span className="text-violet-300">parent account</span>}
                  </div>
                ))}
              </div>
              {result.skipped.length > 0 && (
                <div className="text-xs text-slate-300" data-testid="add-skipped">
                  {result.skipped.map((s, i) => <p key={i}>{s.name}: {s.reason}</p>)}
                </div>
              )}
              <p className="text-xs text-slate-300">
                Starting passwords are shown once. Everyone is asked to choose their own the first
                time they sign in, after that the account is theirs.
              </p>
              <div className="flex gap-2">
                <Button onClick={copyAll} data-testid="copy-credentials"
                  className="rounded-full bg-slate-800 hover:bg-slate-700 text-white">
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy the list
                </Button>
                <Button onClick={() => setResult(null)} data-testid="add-more"
                  className="rounded-full text-slate-900 font-semibold" style={{ background: accent }}>
                  Add more
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Into which program?</Label>
                <Select value={programId} onValueChange={setProgramId}>
                  <SelectTrigger className="input-dark" data-testid="add-program"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Just the roster for now</SelectItem>
                    {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 text-xs">
                {[['paste', 'Paste a list'], ['one', 'One at a time']].map(([k, label]) => (
                  <button key={k} onClick={() => setMode(k)} data-testid={`add-mode-${k}`}
                    className={`rounded-full px-3 py-1 border transition-colors ${mode === k
                      ? 'border-white/40 text-white bg-white/[0.06]' : 'border-white/10 text-slate-300'}`}>
                    {label}
                  </button>
                ))}
              </div>

              {mode === 'paste' ? (
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">
                    One per line: first, last, email, date of birth, parent name, parent email
                  </Label>
                  <Textarea value={paste} onChange={e => setPaste(e.target.value)}
                    className="input-dark min-h-[160px] font-mono text-xs" data-testid="paste-roster"
                    placeholder={"Jordan,Ellis,jordan@example.com,2010-04-11,Dana Ellis,dana@example.com\nRobin,Ellis,,2016-09-02,Dana Ellis,dana@example.com"} />
                  <p className="text-xs text-slate-300">
                    Email and parent details are optional. Under 13 gets no login and waits on a
                    guardian's consent; 13 and over gets a login with a starting password we hand
                    back to you.
                  </p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {[['first_name', 'First name'], ['last_name', 'Last name'],
                    ['email', 'Their email (optional)'], ['date_of_birth', 'Date of birth'],
                    ['grade_level', 'Grade (optional)'], ['parent_name', "Parent's name (optional)"],
                    ['parent_email', "Parent's email (optional)"]].map(([k, label]) => (
                      <div className="space-y-2" key={k}>
                        <Label className="text-slate-300 text-sm">{label}</Label>
                        <Input type={k === 'date_of_birth' ? 'date' : 'text'} value={one[k]}
                          onChange={e => setOne({ ...one, [k]: e.target.value })}
                          className="input-dark" data-testid={`one-${k}`} />
                      </div>
                    ))}
                </div>
              )}

              <Button onClick={add} data-testid="add-participants-submit"
                disabled={saving || (mode === 'paste' ? !paste.trim() : !one.first_name || !one.date_of_birth)}
                className="rounded-full text-slate-900 font-semibold w-full" style={{ background: accent }}>
                {saving ? 'Adding…' : 'Add them'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RosterPanel;
